import { HttpErrorResponse } from "@angular/common/http";
import { ChangeDetectorRef, Injectable } from "@angular/core";
import { FormArray } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { ActivatedRoute, Router } from "@angular/router";
import { BehaviorSubject, combineLatest, Observable, of, ReplaySubject, Subject, Subscription, timer } from "rxjs";
import { distinctUntilChanged, filter, finalize, map, switchMap, take, tap } from "rxjs/operators";
import { ErrorWrapper } from "src/app/lib/error/classes";
import { ErrorDialogComponent } from "src/app/lib/error/components/dialog/error-dialog/error-dialog.component";
import { IConcurrencyProviderService } from "src/app/lib/shared/interfaces";
import { Contract, ContractItem } from "../../../classes";
import { IContractProviderService } from "../../../interfaces";
import { ContractService } from "../../../services/contract.service";

@Injectable()
export class ContractDetailViewComponentService implements IContractProviderService, IConcurrencyProviderService {

    private _contract = new ReplaySubject<Contract>(1);
    contract$: Observable<Contract> = this._contract.asObservable();

    private _contractId = new ReplaySubject<number>(1);
    contractId$: Observable<number> = this._contractId.asObservable();

    private _checkingVersion = new BehaviorSubject(false);
    checkingVersion$ = this._checkingVersion.pipe(tap(() => this._changeDetectorRef.detectChanges()));

    private _loading = new BehaviorSubject<boolean>(false);

    private _currentContractSub: Subscription = null;

    validStamp$: Observable<boolean> = this._contract.pipe(switchMap((x: Contract) => {
        this._checkingVersion.next(true);
        return this._contractService.checkContractVersion(x).pipe(finalize(() => this._checkingVersion.next(false)));
    }));

    private _subscriptions: Subscription[] = [];

    constructor(
        private _contractService: ContractService,
        private _changeDetectorRef: ChangeDetectorRef,
        private _route: ActivatedRoute,
        private _router: Router,
        private _dialog: MatDialog
    ) {
        this.setUp();
    }

    concurrencyStamp$: Observable<string>;
    entityIdentifier$: Observable<any>;

    dispose(): void {
        for (let sub of this._subscriptions) sub.unsubscribe();
        this._subscriptions = [];
        this._unsubscribeCurrentContract();
    }

    deleteCurrentContract(): Observable<Object> {
        let subject = new Subject();
        this.contractId$
            .pipe(take(1), switchMap((contractId: number) => this._contractService.deleteContract(contractId)))
            .subscribe({
                next: () => {
                    this._router.navigate(['/home']);
                    subject.next();
                },
                error: (error: HttpErrorResponse) => subject.error(error),
                complete: () => subject.complete()
            });
        return subject.asObservable();
    }

    refreshCurrentContract(): Observable<Contract> {
        let subject = new Subject<Contract>();
        this._contractId.pipe(
            take(1),
            switchMap((x: number) => {
                return this._contractService.getContractById(x, false);
            })
        ).subscribe(subject);
        return subject.asObservable();
    }

    saveChanges(contract: Contract = null, note: string = null, contractItems: ContractItem[] = [], deleteContractItems: number[] = []): Observable<Contract> {
        return this
            ._contractService
            .updateContract(contract, note)
            .pipe(
                switchMap((result: Contract) => {
                    if (contractItems.length > 0) {
                        return combineLatest(contractItems.map(x => this._contractService[x.itemid ? 'updateContractItem' : 'createContractItem'](x, note)))
                            .pipe(map((args) => result));
                    }
                    else return of(result);
                }),
                switchMap((result: Contract) => {
                    if (deleteContractItems.length > 0) {
                        return combineLatest(deleteContractItems.map((x: number) => this._contractService.deleteContractItem(x))).pipe(map((args) => result));
                    }
                    else return of(result);
                })
            ) as Observable<Contract>;
    }

    setContractId(contractId: number) {
        this._loading.next(true);
        this._unsubscribeCurrentContract();
        this._currentContractSub = this._contractService.getContractById(contractId, false).subscribe({
            next: (contract: Contract) => {
                this._contract.next(contract);
                this._loading.next(false);
            },
            error: (error: HttpErrorResponse) => {
                switch(error.status){
                    case 404:
                        this._dialog.open(ErrorDialogComponent, {
                            panelClass: 'no-padding-dialog-container',
                            disableClose: true,
                            data: {
                                'description': `Es konnte kein Vertrag mit der ID ${contractId} gefunden werden. Ist der Link eventuell veraltet oder wurde der Vertrag in der Zwischenzeit gelöscht?`,
                                'error': error,
                                'showNavigateToMainPage': true
                            } as ErrorWrapper
                        });
                        break;
                }
                this._loading.next(false);
            }
        });
    }

    setUp() {
        this._subscriptions.push(...[
            this._route.params.pipe(map(params => parseInt(params['id'])), filter(x => typeof (x) === 'number')).subscribe((contractId: number) => this._contractId.next(contractId)),
            this.contractId$.pipe(distinctUntilChanged()).subscribe((identifier) => this.setContractId(identifier))
        ]);
    }

    private _unsubscribeCurrentContract() {
        if (this._currentContractSub) {
            this._currentContractSub.unsubscribe();
            this._currentContractSub = null;
        }
    }

}